import React, { useState } from "react";
import logo from "../asico.png";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import axios from "axios";
import { Button } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import FileDownload from "@mui/icons-material/FileDownload";
import dayjs from "dayjs";
dayjs.locale("th");

/**
 * Internal helper to generate a single CPK sheet in the workbook.
 */
async function generateCpkSheet(
  workbook,
  sheetName,
  dataExport,
  partIndexData,
  allTorelance,
  allTorelance2,
  formatTorelance,
  allSpecCount,
  image_logo,
  image_part,
) {
  const worksheet = workbook.addWorksheet(sheetName, {
    pageSetup: {
      paperSize: 9,
      orientation: "portrait",
      scale: 90,
      fitToPage: 1,
      horizontalCentered: true,
      margins: {
        left: 0.0,
        right: 0.0,
        top: 0.4,
        bottom: 0.0,
        header: 0.0,
        footer: 0.0,
      },
    },
  });

  // 1. Fill Spec Count / Part identification
  if (allSpecCount && partIndexData) {
    for (let i = 0; i < partIndexData.length; i++) {
      let col = 4 + 2 * i;
      const cellAddress = `${String.fromCharCode(64 + col)}20`;
      const cell = worksheet.getCell(cellAddress);
      cell.value = allSpecCount[i];
    }
  }

  // 2. Fill Tolerances based on format
  if (allTorelance && allTorelance2 && partIndexData) {
    for (let i = 0; i < partIndexData.length; i++) {
      let col = 4 + 2 * i;
      const cell1 = worksheet.getCell(`${String.fromCharCode(64 + col)}21`);
      const cell2 = worksheet.getCell(`${String.fromCharCode(64 + col)}22`);

      if (formatTorelance === "plusPlus") {
        cell1.value = allTorelance[i];
        cell2.value = allTorelance2[i];
      } else if (formatTorelance === "plusMinus") {
        cell1.value = allTorelance[i];
        cell2.value = -allTorelance2[i];
      } else if (formatTorelance === "minusMinus") {
        cell1.value = -allTorelance[i];
        cell2.value = -allTorelance2[i];
      }
    }
  }

  // 3. Page Layout & Styling
  worksheet.getColumn(1).width = 0;
  const font = { name: "CordiaUPC", size: 16 };

  // Signatures
  if (dataExport.signature_approver) {
    const res = await axios.get(dataExport.signature_approver, {
      responseType: "arraybuffer",
    });
    const imgId = workbook.addImage({ buffer: res.data, extension: "png" });
    worksheet.addImage(imgId, {
      tl: { col: 13.3, row: 67.2 },
      br: { col: 14.8, row: 69.5 },
    });
  }

  if (dataExport.signature_checker) {
    const res = await axios.get(dataExport.signature_checker, {
      responseType: "arraybuffer",
    });
    const imgId = workbook.addImage({ buffer: res.data, extension: "png" });
    worksheet.addImage(imgId, {
      tl: { col: 15.3, row: 67.2 },
      br: { col: 16.8, row: 69.5 },
    });
  }

  // Header & Labels
  worksheet.getCell("B6").value = "Cust. :";
  worksheet.getCell("B7").value = "Model. :";
  worksheet.getCell("B8").value = "P/no.  :";
  worksheet.getCell("B9").value = "P/name.  :";
  worksheet.getCell("B10").value = "Event  :";

  worksheet.mergeCells("B1:S1");
  const valueHeader = worksheet.getCell("B1");
  valueHeader.value = "PROCESS CAPABILITY INDEX";
  valueHeader.style = {
    alignment: { vertical: "middle", horizontal: "center" },
    font: { name: "Cordia UPC", bold: true, size: 18 },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF803" } },
  };

  for (let col = 2; col <= 16; col++) {
    worksheet.getCell(1, col).border = {
      top: { style: "thin" },
      ...(col === 2 ? { left: { style: "thin" } } : {}),
      ...(col === 16 ? { right: { style: "thin" } } : {}),
    };
  }

  // Drawing Sketch Area
  worksheet.getCell("S2").border = { right: { style: "thin" } };
  worksheet.mergeCells("J2:S2");
  const sketchStart = worksheet.getCell("J2");
  sketchStart.value = "DRAWING / SKETCH";
  sketchStart.style = {
    border: {
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
      top: { style: "thin" },
    },
    alignment: { vertical: "middle", horizontal: "center" },
    font: { size: 18, name: "CordiaUPC" },
  };
  ["K2", "L2", "M2", "N2", "O2", "P2", "Q2", "R2", "S2"].forEach((col) => {
    worksheet.getCell(col).style = {
      border: {
        bottom: { style: "thin" },
        top: { style: "thin" },
        right: { style: "thin" },
      },
    };
  });

  worksheet.addImage(image_logo, {
    tl: { col: 1.5, row: 1.5 },
    br: { col: 3, row: 4.2 },
  });
  worksheet.addImage(image_part, {
    tl: { col: 10, row: 3.5 },
    br: { col: 18, row: 17 },
  });

  // Condition Area
  worksheet.mergeCells("F2:I2");
  const condHeader = worksheet.getCell("F2");
  condHeader.value = "CHECK CONDITION";
  condHeader.style = {
    alignment: { vertical: "middle", horizontal: "center" },
    border: {
      bottom: { style: "thin" },
      top: { style: "thin" },
      left: { style: "thin" },
    },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } },
  };
  ["G2", "H2", "I2"].forEach(
    (c) =>
      (worksheet.getCell(c).style = {
        border: { bottom: { style: "thin" }, top: { style: "thin" } },
      }),
  );
  ["C2", "D2", "E2"].forEach(
    (c) =>
      (worksheet.getCell(c).style = { border: { top: { style: "thin" } } }),
  );
  worksheet.getCell("E11").border = { right: { style: "thin" } };
  ["F3", "F4", "F5", "F6", "F7", "F8", "F9"].forEach(
    (c) =>
      (worksheet.getCell(c).style = { border: { left: { style: "thin" } } }),
  );
  ["G10", "H10"].forEach(
    (c) =>
      (worksheet.getCell(c).style = { border: { bottom: { style: "thin" } } }),
  );
  worksheet.getCell("F10").style = {
    border: { bottom: { style: "thin" }, left: { style: "thin" } },
  };
  worksheet.getCell("I10").style = {
    border: { bottom: { style: "thin" }, right: { style: "thin" } },
  };

  const conditions = [
    { cell: "F3", value: "☐   Tool/ Die change." },
    { cell: "F4", value: "☐   Process desgin / Layout change." },
    { cell: "F5", value: "☐   New model /New part." },
    { cell: "F6", value: "☐   First product ." },
    { cell: "F7", value: "☐   Material / Machine change." },
    { cell: "F8", value: "☐   Normal check." },
    { cell: "F9", value: "☐  Other." },
  ];
  conditions.forEach((cond) => {
    worksheet.mergeCells(`${cond.cell}:I${cond.cell.substring(1)}`);
    const cell = worksheet.getCell(cond.cell);
    cell.value = cond.value;
    cell.style = {
      alignment: { vertical: "middle", horizontal: "left" },
      border: {
        left: { style: "thin" },
        ...(cond.cell >= "F5" ? { right: { style: "thin" } } : {}),
      },
      font: font,
    };
  });
  worksheet.mergeCells("F11:I11");
  worksheet.getCell("F11").value = "Detail :";

  // Standard Tables
  const subHeaders = [
    {
      cell: "B12",
      range: "B12:E13",
      val: "Standard for Judgement",
      color: "FFFFBB00",
    },
    { cell: "B14", range: "B14:C14", val: "Item", color: "FFFFBB00" },
    {
      cell: "D14",
      range: "D14:E14",
      val: "Capability Value",
      color: "FFFFBB00",
    },
  ];

  subHeaders.forEach((h) => {
    worksheet.mergeCells(h.range);
    const cell = worksheet.getCell(h.cell);
    cell.value = h.val;
    cell.style = {
      font,
      alignment: { horizontal: "center", vertical: "middle" },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: h.color } },
    };

    // Apply specific borders as requested
    if (h.cell === "B12") {
      // Around Standard for Judgement (B12:E13)
      const [start, end] = h.range.split(":");
      const sCol = start.charCodeAt(0) - 64,
        eCol = end.charCodeAt(0) - 64;
      const sRow = parseInt(start.substring(1)),
        eRow = parseInt(end.substring(1));
      for (let r = sRow; r <= eRow; r++) {
        for (let c = sCol; c <= eCol; c++) {
          worksheet.getCell(r, c).border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        }
      }
    } else if (h.cell === "D14") {
      // Right of Capability value
      const [start, end] = h.range.split(":");
      const eCol = end.charCodeAt(0) - 64;
      const row = parseInt(start.substring(1));
      worksheet.getCell(row, eCol).border = { right: { style: "thin" } };
    }
  });

  worksheet.mergeCells("B15:C16");
  worksheet.getCell("B15").value = "New Part";
  worksheet.getCell("B15").style = {
    font,
    alignment: { horizontal: "left", vertical: "middle" },
  };

  worksheet.mergeCells("B17:C18");
  worksheet.getCell("B17").value = "Current Part";
  worksheet.getCell("B17").style = {
    font,
    alignment: { horizontal: "left", vertical: "middle" },
  };

  worksheet.mergeCells("D15:E16");
  worksheet.getCell("D15").border = {
    left: { style: "thin" },
    right: { style: "thin" },
  };
  worksheet.mergeCells("D17:E18");
  worksheet.getCell("D17").border = {
    left: { style: "thin" },
    right: { style: "thin" },
    bottom: { style: "thin" },
  };

  worksheet.getCell("F15").border = {
    left: { style: "thin" },
  };
  // Inspection Rows
  const tableLabels = [
    { cell: "B19", val: "Inspection Item" },
    { cell: "B20", val: "Spec Cont." },
    { cell: "B21", val: "Tolerance", range: "B21:C22" },
    { cell: "B23", val: "No. /Tool No." },
  ];
  tableLabels.forEach((l) => {
    const range = l.range || `${l.cell}:C${l.cell.substring(1)}`;
    worksheet.mergeCells(range);
    const cell = worksheet.getCell(l.cell);
    cell.value = l.val;
    cell.style = {
      font,
      alignment: { horizontal: "left", vertical: "middle" },
    };

    if (l.cell === "B19") {
      // Right side of Inspection Item (B19:C53) - as requested "start on row 19 till row 53"
      for (let r = 19; r <= 53; r++) {
        const cell = worksheet.getCell(r, 3);
        cell.border = { ...cell.border, right: { style: "thin" } };
      }
    }
  });

  for (let i = 1; i <= 30; i++) {
    const row = 23 + i;
    worksheet.mergeCells(`B${row}:C${row}`);
    worksheet.getRow(row).height = 26;
    const cell = worksheet.getCell(`B${row}`);
    cell.value = i.toString();
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.font = { name: "CordiaUPC", size: 14 };
  }

  // Ensure row 23 and 54 are non-numeric to break vertical adjacency for formulas
  const allDataCols = partIndexData.map((_, idx) => 4 + idx * 2);
  allDataCols.forEach((colIdx) => {
    const colLetter = getColumnLetter(colIdx);
    const cell23 = worksheet.getCell(`${colLetter}23`);
    if (!cell23.value) cell23.value = " ";
    worksheet.getCell(`${colLetter}54`).value = " ";
  });

  // Footers
  const footerItems = [
    { row: 55, val: "MINIMUM" },
    { row: 56, val: "MAXIMUM" },
    { row: 57, val: "AVERAGE" },
    { row: 58, val: "STDEV" },
    { row: 59, val: "USL" },
    { row: 60, val: "LSL" },
    { row: 61, val: "☐ Cp ☐ Pp" },
    { row: 62, val: "☐ Cpu ☐ Ppu" },
    { row: 63, val: "☐ Cpl ☐ Ppl" },
    { row: 64, val: "☐ Cpk ☐ Ppk" },
    { row: 65, val: "JUDGEMENT" },
  ];
  footerItems.forEach((item) => {
    worksheet.mergeCells(`B${item.row}:C${item.row}`);
    worksheet.getCell(`B${item.row}`).value = item.val;
    worksheet.getCell(`B${item.row}`).alignment = {
      horizontal: "center",
      vertical: "middle",
    };
  });

  worksheet.mergeCells("B66:J66");
  worksheet.getCell("B66").value =
    "REMARK : -this format for tolerance both side same & torelance both side not same";

  const approvalHeaders = [
    { cell: "L66", val: "JUDGEMENT" },
    { cell: "N66", val: "Approved" },
    { cell: "P66", val: "Checked" },
    { cell: "R66", val: "Issued" },
  ];
  approvalHeaders.forEach((h) => {
    const range = `${h.cell}:${String.fromCharCode(h.cell.charCodeAt(0) + 1)}66`;
    worksheet.mergeCells(range);
    const cell = worksheet.getCell(h.cell);
    cell.value = h.val;
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF98FB98" },
    };
  });

  for (let r = 59; r <= 65; r++) {
    for (let c = 2; c <= 19; c++) {
      worksheet.getCell(r, c).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF98FB98" },
      };
    }
  }

  ["L67:M70", "N67:O70", "P67:Q70", "R67:S70"].forEach((range) =>
    worksheet.mergeCells(range),
  );

  // Styling & Borders
  for (let i = 3; i <= 10; i++) worksheet.getRow(i).height = 26;
  for (let i = 55; i <= 67; i++) worksheet.getRow(i).height = 28;
  [19, 20, 21, 22, 23].forEach(
    (r) => (worksheet.getRow(r).height = r === 19 ? 35 : 25),
  );

  for (let c = 2; c <= 18; c++)
    worksheet.getCell(53, c).border = {
      bottom: { style: "thin" },
      right: { style: "thin" },
      left: { style: "thin" },
    };
  worksheet.getCell("S53").border = {
    bottom: { style: "thin" },
    right: { style: "thin" },
  };
  worksheet.getCell("S55").border = {
    top: { style: "thin" },
    right: { style: "thin" },
  };
  ["S61", "S65", "S66"].forEach(
    (c) =>
      (worksheet.getCell(c).border = {
        right: { style: "thin" },
        ...(c === "S66" ? { bottom: { style: "thin" } } : {}),
      }),
  );
  worksheet.getCell("R65").border = {
    right: { style: "thin" },
    bottom: { style: "thin" },
    left: { style: "thin" },
  };
  ["N66", "P66", "L66"].forEach(
    (c) =>
      (worksheet.getCell(c).border = {
        right: { style: "thin" },
        bottom: { style: "thin" },
        ...(c === "L66" ? { left: { style: "thin" } } : {}),
      }),
  );
  worksheet.getCell("L69").border = { left: { style: "thin" } };
  worksheet.getCell("S70").border = {
    bottom: { style: "thin" },
    right: { style: "thin" },
  };
  worksheet.getCell("B70").border = {
    bottom: { style: "thin" },
    left: { style: "thin" },
  };
  ["G18", "F18", "H18"].forEach(
    (c) => (worksheet.getCell(c).border = { bottom: { style: "thin" } }),
  );
  worksheet.getCell("I18").border = {
    bottom: { style: "thin" },
    right: { style: "thin" },
  };

  for (let i = 19; i <= 65; i++) {
    ["D", "F", "H", "J", "L", "N", "P", "R"].forEach((col) => {
      const target =
        i <= 53 || i >= 55
          ? `${col}${i}:${String.fromCharCode(col.charCodeAt(0) + 1)}${i}`
          : null;
      if (target) worksheet.mergeCells(target);
    });
  }

  for (let i = 19; i <= 53; i++) {
    ["E", "G", "I", "K", "M", "O", "Q", "S"].forEach(
      (c) =>
        (worksheet.getCell(`${c}${i}`).border = {
          ...worksheet.getCell(`${c}${i}`).border,
          right: { style: "thin" },
        }),
    );
    worksheet.getCell(`B${i}`).border = {
      ...worksheet.getCell(`B${i}`).border,
      left: { style: "thin" },
      ...(i >= 24 && i % 3 === 0 ? { top: { style: "thin" } } : {}),
    };
    worksheet.getCell(`D${i}`).border = {
      ...worksheet.getCell(`D${i}`).border,
      left: { style: "thin" },
    };
  }

  for (let c = 3; c <= 11; c++)
    worksheet.getCell(70, c).border = { bottom: { style: "thin" } };
  for (let c = 11; c <= 17; c++)
    worksheet.getCell(70, c).border = {
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  for (let i = 64; i <= 69; i++)
    worksheet.getCell(`K${i}`).border = { right: { style: "thin" } };
  for (let c = 2; c <= 19; c++)
    worksheet.getCell(55, c).border = {
      top: { style: "thin" },
      left: { style: "thin" },
      ...(c === 19 ? { right: { style: "thin" } } : {}),
    };
  for (let r = 56; r <= 69; r++)
    if (r !== 65)
      worksheet.getCell(`B${r}`).border = { left: { style: "thin" } };
  worksheet.getCell("B65").border = {
    bottom: { style: "thin" },
    left: { style: "thin" },
  };
  for (let i = 3; i <= 52; i++)
    worksheet.getCell(`S${i}`).border = { right: { style: "thin" } };
  worksheet.getCell("B1").border = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
  };
  worksheet.getCell("B14").border = {
    top: { style: "thin" },
    left: { style: "thin" },
  };
  worksheet.getCell("B12").border = {
    top: { style: "thin" },
    right: { style: "thin" },
    left: { style: "thin" },
  };
  worksheet.getCell("D14").border = {
    top: { style: "thin" },
    right: { style: "thin" },
    left: { style: "thin" },
  };

  for (let i = 3; i <= 53; i++)
    if (i !== 18 && i !== 10 && i !== 53)
      worksheet.getCell(`I${i}`).border = { right: { style: "thin" } };
  for (let r = 56; r <= 64; r++) {
    for (let c = 4; c <= 19; c++)
      worksheet.getCell(r, c).border = {
        left: { style: "thin" },
        ...(c === 19 ? { right: { style: "thin" } } : {}),
      };
  }
  for (let c = 5; c <= 16; c++)
    worksheet.getCell(65, c).border = {
      bottom: { style: "thin" },
      left: { style: "thin" },
    };

  for (let r = 19; r <= 65; r++) {
    for (let c = 4; c <= 19; c++)
      worksheet.getCell(r, c).alignment = {
        vertical: "middle",
        horizontal: "center",
      };
  }

  // 4. Data Population
  if (dataExport.dataquestion && partIndexData) {
    const startCol = 4;
    partIndexData.forEach((item, idx) => {
      const col = startCol + idx * 2;
      const colL = getColumnLetter(col);

      // Actuals
      item.actual1.forEach((val, rIdx) => {
        const dataCell = worksheet.getCell(`${colL}${24 + rIdx}`);
        dataCell.value = val;
        // Add top border for the first data row (Row 24)
        if (rIdx === 0) {
          dataCell.border = { ...dataCell.border, top: { style: "thin" } };
        }
      });

      worksheet.getCell(`${colL}19`).value = item.checkPoint;
      worksheet.getCell(`${colL}19`).border = {
        top: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
      worksheet.getCell(`${colL}19`).alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      worksheet.getCell(`${colL}23`).value = item.inspectionTool;

      const range = `${colL}24:${colL}53`;
      worksheet.getCell(`${colL}55`).value = { formula: `MIN(${range})` };
      worksheet.getCell(`${colL}56`).value = { formula: `MAX(${range})` };

      // Formulas
      const statsRange = `${colL}24:${colL}53`;
      worksheet.getCell(`${colL}57`).value = {
        formula: `AVERAGE(${statsRange})`,
      };
      worksheet.getCell(`${colL}58`).value = {
        formula: `STDEV(${statsRange})`,
      };
      worksheet.getCell(`${colL}59`).value = {
        formula: `IF(AND(${colL}20<>"",${colL}21<>""),(${colL}20+${colL}21),"")`,
      };
      worksheet.getCell(`${colL}60`).value = {
        formula: `IF(AND(${colL}20<>"",${colL}22<>""),(${colL}20+${colL}22),"")`,
      };
      worksheet.getCell(`${colL}61`).value = {
        formula: `IF(AND(${colL}59<>"",${colL}60<>""),(${colL}59-${colL}60)/(6*${colL}58),"")`,
      };
      worksheet.getCell(`${colL}62`).value = {
        formula: `IF(AND(${colL}24<>"",${colL}59<>""),(${colL}59-${colL}57)/(3*${colL}58),"")`,
      };
      worksheet.getCell(`${colL}63`).value = {
        formula: `IF(AND(${colL}24<>"",${colL}60<>""),(${colL}57-${colL}60)/(3*${colL}58),"")`,
      };
      worksheet.getCell(`${colL}64`).value = {
        formula: `IF(AND(${colL}62<>"",${colL}63<>""),MIN(${colL}62,${colL}63),"")`,
      };
      const jud = worksheet.getCell(`${colL}65`);
      jud.alignment = { horizontal: "center", vertical: "middle" };
      jud.value = {
        formula: `IF(AND(${colL}64<>"",${colL}64>=1.67),"О","X")`,
      };

      [57, 58, 59, 60, 61, 62, 63, 64].forEach(
        (r) => (worksheet.getCell(`${colL}${r}`).numFmt = "0.00"),
      );
    });
  }

  const judgementCell = worksheet.getCell("L67");
  judgementCell.value = {
    formula: `IF(AND(D64<>"",F64<>"",H64<>"",J64<>"",L64<>"",N64<>"",P64<>"",R64<>"",MIN(D64,F64,H64,J64,L64,N64,P64,R64)>=1.67),"О","X")`,
  };
  judgementCell.font = { size: 20 };
  judgementCell.alignment = { horizontal: "center", vertical: "middle" };
  judgementCell.border = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };
}

// Helper to convert column index to Excel letter (A, B, C...)
function getColumnLetter(index) {
  let letter = "";
  let tempIndex = index - 1;
  while (tempIndex >= 0) {
    letter = String.fromCharCode((tempIndex % 26) + 65) + letter;
    tempIndex = Math.floor(tempIndex / 26) - 1;
  }
  return letter;
}

/**
 * Main export function called by UI buttons
 */
export async function export_cpk2(
  dataExport,
  partIndex,
  partIndex2,
  allTorelance,
  allTorelance2,
  formatTorelance,
  allSpecCount,
  page,
) {
  const workbook = new ExcelJS.Workbook({ useStyle: true });

  // Load common assets once
  const logoRes = await axios.get(logo, { responseType: "arraybuffer" });
  const image_logo = workbook.addImage({
    buffer: logoRes.data,
    extension: "png",
  });

  const partRes = await axios.get(dataExport.imagePart, {
    responseType: "arraybuffer",
  });
  const image_part = workbook.addImage({
    buffer: partRes.data,
    extension: "png",
  });

  // Generate Sheets
  await generateCpkSheet(
    workbook,
    `sheet ${page > 1 ? page - 1 : 1}`,
    dataExport,
    partIndex,
    allTorelance,
    allTorelance2,
    formatTorelance,
    allSpecCount,
    image_logo,
    image_part,
  );

  if (page === 2 && partIndex2) {
    await generateCpkSheet(
      workbook,
      `sheet ${page}`,
      dataExport,
      partIndex2,
      allTorelance,
      allTorelance2,
      formatTorelance,
      allSpecCount,
      image_logo,
      image_part,
    );
  }

  // Download
  const buffer = await workbook.xlsx.writeBuffer();
  const mapTypeTorelance = {
    plusPlus: "+$+",
    minusMinus: "-$-",
    plusMinus: "+$-",
  };
  const fileName = `${dataExport.partName}_CPK_${dayjs().format("DD/MM/YYYY")}${mapTypeTorelance[formatTorelance] || ""}Worksheet_${page}.xlsx`;
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, fileName);
}

const ExportCPKSheet = ({ data, isReady }) => {
  const [disabled, setDisabled] = useState(true);
  return (
    <div className="App">
      <Button
        disabled={disabled}
        onClick={() => export_cpk2(data)}
        variant="outlined"
        endIcon={<Icon isReady={isReady} setDisabled={setDisabled} />}
      >
        download
      </Button>
    </div>
  );
};

export default ExportCPKSheet;

const Icon = ({ isReady, setDisabled }) => {
  if (isReady === undefined) {
    setDisabled(false);
    return <FileDownload />;
  }
  if (isReady) {
    setDisabled(false);
    return <FileDownload />;
  }
  setDisabled(true);
  return <CircularProgress size={20} />;
};
